import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { categoryService } from '../services/api';
import CartSidebar from './CartSidebar';

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { getTotalItems } = useCart();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [categories, setCategories] = useState([]);
  const menuRef = useRef(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoryService.getAll();
        setCategories(response?.data?.data || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    setShowMobileMenu(false);
  };

  const handleLogout = () => {
    logout();
    setShowMobileMenu(false);
  };

  return (
    <>
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div className="flex justify-around items-center h-16 relative" dir="rtl">
          {/* Store */}
          <Link
            to="/products"
            className={`flex flex-col items-center justify-center flex-1 h-full ${
              isActive('/products') ? 'text-primary' : 'text-gray-600'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <span className="text-xs mt-1">المتجر</span>
          </Link>

          {/* Orders */}
          <Link
            to="/orders"
            className={`flex flex-col items-center justify-center flex-1 h-full ${
              isActive('/orders') ? 'text-primary' : 'text-gray-600'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span className="text-xs mt-1">الطلبات</span>
          </Link>

          {/* Spacer for Home Button */}
          <div className="flex-[0.01]"></div>

          {/* Home Button (Center) */}
          <Link
            to="/"
            className="absolute left-1/2 transform -translate-x-1/2 -top-6"
          >
            <div className="w-16 h-16 rounded-full shadow-lg flex items-center justify-center bg-white hover:bg-gray-50 transition-colors p-2">
              <img 
                src="/icon.png" 
                alt="Logo" 
                className="w-full h-full object-contain"
              />
            </div>
          </Link>

          {/* Spacer for Home Button */}
          <div className="flex-1"></div>

          {/* Cart */}
          <button
            onClick={() => setIsCartOpen(true)}
            className={`flex flex-col items-center justify-center flex-1 h-full relative ${
              isActive('/cart') ? 'text-primary' : 'text-gray-600'
            }`}
          >
            <div className="relative">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {getTotalItems() > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
                  {getTotalItems()}
                </span>
              )}
            </div>
            <span className="text-xs mt-1">السلة</span>
          </button>

          {/* Menu */}
          <div className="relative flex-1 h-full flex items-center justify-center" ref={menuRef}>
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className={`flex flex-col items-center justify-center w-full h-full ${
                showMobileMenu ? 'text-primary' : 'text-gray-600'
              }`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span className="text-xs mt-1">القائمة</span>
            </button>

            {/* Mobile Menu Dropdown */}
            {showMobileMenu && (
              <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-64 bg-white border border-gray-200 rounded-lg shadow-2xl z-50 max-h-[70vh] overflow-y-auto">
                {/* Categories Section */}
                <div className="border-b border-gray-200">
                  <div className="px-4 py-3 bg-gray-50 font-semibold text-sm text-gray-800">
                    الأقسام
                  </div>
                  <div className="space-y-0">
                    {categories.length > 0 ? (
                      categories.map(category => (
                        <button
                          key={category.id}
                          onClick={() => handleMenuItemClick(`/products?category=${category.id}`)}
                          className="w-full text-right px-4 py-3 text-gray-700 hover:bg-gray-50 text-sm transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          {category.name}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-gray-500 text-sm text-center">جاري التحميل...</div>
                    )}
                  </div>
                </div>

                {/* User Section */}
                <div className="border-b border-gray-200">
                  {isAuthenticated ? (
                    <>
                      <div className="px-4 py-2 bg-gray-50 text-sm font-semibold text-gray-800">
                        مرحباً، {user?.full_name || 'مستخدم'}
                      </div>
                      <button
                        onClick={() => handleMenuItemClick('/profile')}
                        className="w-full text-right px-4 py-3 text-gray-700 hover:bg-gray-50 text-sm border-b border-gray-100 transition-colors"
                      >
                        الملف الشخصي
                      </button>
                      <button
                        onClick={() => handleMenuItemClick('/orders')}
                        className="w-full text-right px-4 py-3 text-gray-700 hover:bg-gray-50 text-sm border-b border-gray-100 transition-colors"
                      >
                        طلباتي
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full text-right px-4 py-3 text-red-600 hover:bg-red-50 text-sm transition-colors"
                      >
                        تسجيل الخروج
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleMenuItemClick('/login')}
                        className="w-full text-right px-4 py-3 text-gray-700 hover:bg-gray-50 text-sm border-b border-gray-100 transition-colors"
                      >
                        تسجيل دخول
                      </button>
                      <button
                        onClick={() => handleMenuItemClick('/register')}
                        className="w-full text-right px-4 py-3 text-primary hover:bg-blue-50 font-semibold text-sm transition-colors"
                      >
                        إنشاء حساب
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Cart Sidebar */}
      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </>
  );
};

export default MobileBottomNav;
